import { getToken, onMessage } from 'firebase/messaging';
import { getFirebaseMessaging } from '../firebase';
import axios from 'axios';
import { API_BASE_URL as BASE_URL } from './config';
const VAPID_KEY = 'BPsujI8DNDRY2UKU3rBEUPGzJu6PY-O2cNfXUgcHjA34aJM3lmNdhH4DYGh_xy61d8iq-Tbn8vZkEAy9o4WI9fg';

function getAuthHeader() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const webFcmService = {
  async init(): Promise<void> {
    try {
      // Step 1: Check / request permission
      console.log('[FCM] Current permission:', Notification.permission);
      if (Notification.permission === 'denied') {
        console.warn('[FCM] Notifications blocked by user in browser settings');
        return;
      }
      if (Notification.permission !== 'granted') {
        const result = await Notification.requestPermission();
        console.log('[FCM] Permission requested, result:', result);
        if (result !== 'granted') return;
      }

      // Step 2: Get messaging instance (checks browser support)
      const messagingInstance = await getFirebaseMessaging();
      if (!messagingInstance) return;

      // Step 3: Register service worker explicitly (required for getToken)
      let swReg: ServiceWorkerRegistration | undefined;
      try {
        swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
        console.log('[FCM] Service worker registered at scope:', swReg.scope);
      } catch (swErr) {
        console.error('[FCM] Service worker registration FAILED:', swErr);
        return;
      }

      // Step 4: Get FCM token
      const token = await getToken(messagingInstance, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: swReg,
      });

      if (!token) {
        console.warn('[FCM] getToken() returned empty — check VAPID key and Firebase config');
        return;
      }

      console.log('[FCM] Token obtained:', token.substring(0, 20) + '...');

      // Step 5: Skip if same token already confirmed registered
      const existing = localStorage.getItem('fcmToken');
      if (existing === token) {
        console.log('[FCM] Token unchanged, skipping registration');
        return;
      }

      // Step 6: Register with backend — save to localStorage ONLY after success
      await axios.post(
        `${BASE_URL}/fcm/register-token`,
        { token, platform: 'web' },
        { headers: getAuthHeader() },
      );
      localStorage.setItem('fcmToken', token);
      console.log('[FCM] Token registered with backend successfully');
    } catch (e) {
      console.error('[FCM] init error:', e);
    }
  },

  async removeToken(): Promise<void> {
    try {
      const token = localStorage.getItem('fcmToken');
      if (token) {
        await axios.post(
          `${BASE_URL}/fcm/remove-token`,
          { token },
          { headers: getAuthHeader() },
        );
        localStorage.removeItem('fcmToken');
      }
    } catch (e) {
      console.warn('[FCM] removeToken error:', e);
    }
  },

  async onForegroundMessage(
    handler: (title: string, body: string, data: Record<string, string>) => void,
  ): Promise<() => void> {
    const messagingInstance = await getFirebaseMessaging();
    if (!messagingInstance) return () => {};
    return onMessage(messagingInstance, (payload) => {
      const title = payload.notification?.title ?? 'Notification';
      const body = payload.notification?.body ?? '';
      const data = (payload.data ?? {}) as Record<string, string>;
      handler(title, body, data);
    });
  },

  async sendByRole(
    restaurantId: string,
    roles: string[],
    title: string,
    body: string,
    data?: Record<string, string>,
  ): Promise<number> {
    const res = await axios.post<{ tokenCount: number }>(
      `${BASE_URL}/fcm/send-by-role`,
      { restaurantId, roles, title, body, data: data ?? {} },
      { headers: getAuthHeader() },
    );
    return res.data.tokenCount ?? 0;
  },
};
